import React from 'react'

export const DEFAULT_PAGE_SIZES = [50, 100, 200, 500]

/**
 * Shared pager for long tables: first/prev/page number/next/last, record range, total and page size.
 * `total` is the full filtered count (not just the visible page).
 */
function PageNumberField({ page, pageCount, loading, onCommit }) {
  const [draft, setDraft] = React.useState(String(page))
  React.useEffect(() => { setDraft(String(page)) }, [page])
  return (
    <input
      type="number"
      min={1}
      max={pageCount}
      value={draft}
      disabled={loading}
      aria-label="Page number"
      className="input-field w-16 py-1 text-center tabular-nums"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => onCommit(draft)}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return
        e.preventDefault()
        onCommit(draft)
      }}
    />
  )
}

export default function TablePager({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizes = DEFAULT_PAGE_SIZES,
  loading = false,
  noun = 'records',
  className = '',
}) {
  if (total == null || total <= 0) return null
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const from = (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)
  const go = (p) => onPageChange(Math.min(pageCount, Math.max(1, p)))
  return (
    <nav className={`flex flex-wrap items-center gap-2 text-sm ${className}`} aria-label="Pagination">
      <button type="button" className="btn-secondary" disabled={loading || page <= 1} onClick={() => go(1)} aria-label="First page">«</button>
      <button type="button" className="btn-secondary" disabled={loading || page <= 1} onClick={() => go(page - 1)}>Previous</button>
      <span className="inline-flex items-center gap-1 text-gray-600 tabular-nums" aria-live="polite">
        {from}–{to} of {total.toLocaleString()} {noun}
        <span className="inline-flex items-center gap-1">
          · Page
          <PageNumberField page={page} pageCount={pageCount} loading={loading} onCommit={(raw) => {
            const n = Number(raw)
            if (Number.isFinite(n)) go(Math.trunc(n))
          }} />
          of {pageCount}
        </span>
      </span>
      <button type="button" className="btn-secondary" disabled={loading || page >= pageCount} onClick={() => go(page + 1)}>Next</button>
      <button type="button" className="btn-secondary" disabled={loading || page >= pageCount} onClick={() => go(pageCount)} aria-label="Last page">»</button>
      {onPageSizeChange && (
        <label className="ml-2 flex items-center gap-1 text-gray-600">
          Rows
          <select
            className="input-field py-1"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {pageSizes.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      )}
      {loading && <span className="text-gray-400">Loading…</span>}
    </nav>
  )
}
